/**
 * 테넌트(회사) 프리셋 — 프론트(Vue)·백엔드(Express) 공용 CommonJS.
 *
 * 회사 코드별로 화면 계열(layout)·첫 화면(homeRoute)·자산맵 방식·병동 구성을 여기서만 정한다.
 * 컴포넌트/라우터/API 는 `company === 'SDAA'` 같은 비교 대신 이 프리셋을 본다.
 *
 *  layout   : 'sdaa'   병원 병동형 화면 (병동 버튼 · 장비 카드 · 위치 제한 규칙)
 *             'solum'  존 기반 자산 대시보드 (3D 빌딩 · 업로드 도면 자산맵)
 *             'default' 기본 대시보드
 *  assetMap : 'uploaded'    도면 업로드 + 게이트웨이 드래그 배치 (AssetMapSolumPage)
 *             'sdaa-static' SDAA 전용 고정 도면·좌표 (AssetMapSDAA) — SDAA 만
 *             'post'        우편 물류 전용 (AssetMapPost)
 *             'default'     기본 (AssetMap.vue)
 *  wards    : 병동형 화면의 병동 목록. code(버튼 표시) · label · prefix(장비명 접두사, 예 '73_')
 *             storage(병동 보관 위치 — 대시보드 '사용가능' 집계 기준) · rooms(허용 위치 — 존 판정 시 이 밖으로는 이동 불가)
 *             비어 있으면 병동 구분 없이 전체 장비를 보여준다.
 */

const range = (from, to, pad) => Array.from({ length: to - from + 1 }, (_, i) => String(from + i).padStart(pad || 0, '0'));

const WARDS_SDAA = [
  { code: '73', label: '73병동', prefix: '73_', floor: '07', storage: ['73보관실', '7층_간이주방'],
    rooms: [...range(3701, 3712), '73보관실', '7층_간이주방'] },
  { code: '74', label: '74병동', prefix: '74_', floor: '07', storage: ['74보관실', '7층_간이주방'],
    rooms: [...range(3751, 3762), '74보관실', '7층_간이주방'] },
  { code: '83', label: '83병동', prefix: '83_', floor: '08', storage: ['83보관실', '83간호사실'],
    rooms: [...range(3801, 3836), '83간호사실'] },
];

const BASE_FEATURES = { assetMap: true, rtHistory: false, otaMenu: true, notifications: true, tagRegisterPost: false };

const PRESETS = {
  default: {
    id: 'default', displayName: 'default', layout: 'default', homeRoute: '/dashboard', assetMap: 'default', lang: null,
    hospitalName: null, wards: [], logo: null, hideCompanyName: false, showBuilding: true,
    features: { ...BASE_FEATURES },
  },
  SOLUM: {
    id: 'SOLUM', displayName: 'SOLUM', layout: 'solum', homeRoute: '/dashboardSolum', assetMap: 'uploaded', lang: null,
    hospitalName: null, wards: [], logo: null, hideCompanyName: false, showBuilding: true,
    features: { ...BASE_FEATURES, otaMenu: false },
  },
  POST: {
    id: 'POST', displayName: 'POST', layout: 'default', homeRoute: '/dashboard', assetMap: 'post', lang: null,
    hospitalName: null, wards: [], logo: null, hideCompanyName: false, showBuilding: true,
    features: { ...BASE_FEATURES, rtHistory: true, tagRegisterPost: true },
  },
  PNK: {
    id: 'PNK', displayName: 'PNK', layout: 'default', homeRoute: '/dashboard', assetMap: 'assetMap2', lang: null,
    hospitalName: null, wards: [], logo: null, hideCompanyName: false, showBuilding: true,
    features: { ...BASE_FEATURES },
  },
  // 강남세브란스(SDAA): 병동형 화면 + 고정 도면 자산맵. 계약 종료 상태이나 데이터 열람용으로 유지.
  SDAA: {
    id: 'SDAA', displayName: 'SDAA', layout: 'sdaa', homeRoute: '/oldTagList', assetMap: 'sdaa-static', lang: null,
    hospitalName: '강남세브란스병원', wards: WARDS_SDAA, logo: 'SDAA.png', hideCompanyName: true, showBuilding: false,
    features: { ...BASE_FEATURES, notifications: false },
  },
  // 을지대학교병원(온프레미스 납품): SDAA 와 같은 병동형 화면, 자산맵은 도면 업로드 방식.
  // TODO 병동 코드가 확정되면 wards 에 { code, label, prefix, storage, rooms } 를 추가한다 (장비명은 `${prefix}장비종류_번호` 규칙).
  EULJI: {
    id: 'EULJI', displayName: '을지대학교병원', layout: 'sdaa', homeRoute: '/oldTagList', assetMap: 'uploaded', lang: 'ko',
    hospitalName: '을지대학교병원', wards: [], logo: null, hideCompanyName: false, showBuilding: false,
    features: { ...BASE_FEATURES, notifications: true },
  },
};

// SOLUM 계열 (전용 대시보드 라우트만 다름)
const SOLUM_INHERIT = {
  FPT:     { homeRoute: '/dashboardFPT', showBuilding: false },
  INDIA:   { homeRoute: '/dashboardINDIA', showBuilding: false, lang: 'en' },
  HYU:     { homeRoute: '/dashboardHYU' },
  SSH:     { homeRoute: '/dashboardSSH' },
  DAEDUCK: { homeRoute: '/dashboardDaeduck' },
  SALES:   { homeRoute: '/dashboardSALES', showBuilding: false },   // SKAIChips 영업팀 데모 회사
};
const OPERATORS = ['skaichips'];

function resolveTenant(company) {
  const key = String(company || '').trim().toUpperCase();
  if (PRESETS[key]) return { ...PRESETS[key], features: { ...PRESETS[key].features } };
  if (SOLUM_INHERIT[key]) {
    return { ...PRESETS.SOLUM, ...SOLUM_INHERIT[key], id: key, displayName: key, inherit: 'SOLUM', features: { ...PRESETS.SOLUM.features } };
  }
  return { ...PRESETS.default, id: key || 'default', displayName: key || 'default', features: { ...PRESETS.default.features } };
}

// 장비명(devicename)이 속한 병동 (prefix 기준). 없으면 null.
function wardOf(tenant, devicename) {
  const name = String(devicename || '');
  return (tenant?.wards || []).find((w) => w.prefix && name.startsWith(w.prefix)) || null;
}

function wardByCode(tenant, code) {
  return (tenant?.wards || []).find((w) => w.code === String(code)) || null;
}

function isOperator(company) {
  return OPERATORS.includes(String(company || '').trim().toLowerCase());
}

function canAccessCompany(userCompany, reqCompany) {
  if (isOperator(userCompany)) return true;
  return String(userCompany || '').trim().toLowerCase() === String(reqCompany || '').trim().toLowerCase();
}

module.exports = { PRESETS, WARDS_SDAA, resolveTenant, wardOf, wardByCode, isOperator, canAccessCompany };
